import { Allergen } from "@dining/api-client";
import { Image, Text, makeStyles } from "@fluentui/react-components";
import classNames from "classnames";
import * as React from "react";

import { allergenDataToString, getPriceString } from "../../common/dataConversions";
import menuItemPlaceholder from "../../images/menuItemPlaceholder.png";
import styles from "../../styles/menuPreview.module.css";
import { PreviewMenuItem } from "./types/menuPreviewTypes";

export interface MenuItemCardProps {
  menuItem: PreviewMenuItem;
  onSelect: () => void;
  selected: boolean;
  stationId: string;
  cafeId: string;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  image: {
    width: "90px",
    height: "90px",
    flexShrink: 0,
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
  header: {
    fontWeight: 600,
  },
});

const MenuItemCard: React.FC<MenuItemCardProps> = ({ menuItem, onSelect, selected, stationId, cafeId }) => {
  const classes = useStyles();
  const itemData = menuItem.itemData;

  const allergens: Allergen[] = [];
  const optionalAllergens: Allergen[] = [];

  let imageSrc = menuItemPlaceholder;
  let imageAltText = "";

  if (itemData?.imageUrl) {
    imageSrc = itemData.imageUrl;
    imageAltText = `Image of ${itemData.name} ${itemData.description ?? ""}`;
  }

  itemData?.allergens?.forEach((allergen) => {
    allergen.optional ? optionalAllergens.push(allergen) : allergens.push(allergen);
  });

  const allergenString = allergenDataToString(allergens);
  const optionalAllergenString = allergenDataToString(optionalAllergens);

  return (
    <div className={classNames(classes.root, styles.menuItemCard)} onClick={onSelect} role="button">
      <Image className={classes.image} src={imageSrc} alt={imageAltText} fit="cover" />

      <div className={classes.content}>
        <Text className={classNames(classes.header, styles.menuItemHeader)}>
          {itemData?.name}
          {itemData?.calories && itemData.calories > -1 && (
            <span className={styles.calories}> ({itemData.calories} cal.)</span>
          )}
          {!menuItem.isPublished && <span> *</span>}
        </Text>

        {itemData?.description && <Text>{itemData.description}</Text>}

        <div className={styles.allergen}>
          {allergenString && <span>Contains: {allergenString}.</span>}
          {optionalAllergenString && <span> Optional ingredients: {optionalAllergenString}.</span>}
        </div>

        <Text className={styles.price}>{getPriceString(itemData?.pricing)}</Text>
      </div>
    </div>
  );
};

export default MenuItemCard;
